import {
  layoutBreadcrumbMessage,
} from "./BreadcrumbMessageLayout";
import {
  SYMBOL_SEQUENCE_FONT_KEY,
  SYMBOL_SEQUENCE_FONT_SIZE,
} from "./SymbolSequenceFont";

const MESSAGE_Y_OFFSET = 32;
const MESSAGE_BG_PADDING = 4;
const MESSAGE_BG_COLOR = 0x000000;
const MESSAGE_BG_ALPHA = 0.7;
const MESSAGE_MAX_WIDTH_INSET = 32;
const MESSAGE_TEXT_DEPTH = 51;

export class BreadcrumbMessageRenderer {
  private readonly scene: Phaser.Scene;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly glyphPool: Phaser.GameObjects.BitmapText[] = [];
  private readonly viewportWidth: number;
  private readonly viewportHeight: number;
  private during: number = 0;
  private elapsedMs: number = 0;
  private dismissOnAction: boolean = false;
  private active: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const { width, height } = scene.cameras.main;
    this.viewportWidth = width;
    this.viewportHeight = height;

    this.bg = scene.add.graphics();
    this.bg.setScrollFactor(0);
    this.bg.setDepth(50);
  }

  public show(text: string, during: number): void {
    if (text.trim().length === 0) {
      this.dismiss();
      return;
    }

    this.during = during;
    this.elapsedMs = 0;
    this.dismissOnAction = during === -1;
    this.active = true;

    const layout = layoutBreadcrumbMessage(text, {
      maxWidth: this.viewportWidth - MESSAGE_MAX_WIDTH_INSET,
    });
    this.renderLayout(layout);
  }

  public notifyAction(): void {
    if (this.dismissOnAction) {
      this.dismiss();
    }
  }

  public update(delta: number): void {
    if (!this.active) return;

    if (this.during > 0) {
      this.elapsedMs += delta;
      if (this.elapsedMs >= this.during * 1000) {
        this.dismiss();
      }
    }
  }

  private dismiss(): void {
    this.active = false;
    this.dismissOnAction = false;
    for (const glyph of this.glyphPool) {
      glyph.setVisible(false);
      glyph.setAlpha(0);
      glyph.setText("");
    }
    this.bg.clear();
  }

  private renderLayout(layout: ReturnType<typeof layoutBreadcrumbMessage>): void {
    this.bg.clear();
    if (!this.active) return;

    const blockLeft = Math.floor((this.viewportWidth - layout.width) / 2);
    const blockTop = Math.floor(this.viewportHeight - MESSAGE_Y_OFFSET - layout.height / 2);
    let glyphIndex = 0;

    for (const line of layout.lines) {
      const lineLeft = Math.floor((this.viewportWidth - line.width) / 2);
      for (const glyph of line.glyphs) {
        const glyphText = this.getGlyph(glyphIndex);
        glyphText.setText(glyph.char);
        glyphText.setPosition(lineLeft + glyph.x, blockTop + glyph.y);
        glyphText.setVisible(true);
        glyphText.setAlpha(1);
        glyphIndex += 1;
      }
    }

    for (let index = glyphIndex; index < this.glyphPool.length; index += 1) {
      const glyph = this.glyphPool[index];
      glyph?.setVisible(false);
      glyph?.setAlpha(0);
      glyph?.setText("");
    }

    this.bg.fillStyle(MESSAGE_BG_COLOR, MESSAGE_BG_ALPHA);
    this.bg.fillRect(
      blockLeft - MESSAGE_BG_PADDING,
      blockTop - MESSAGE_BG_PADDING,
      layout.width + MESSAGE_BG_PADDING * 2,
      layout.height + MESSAGE_BG_PADDING * 2,
    );
  }

  private getGlyph(index: number): Phaser.GameObjects.BitmapText {
    const existing = this.glyphPool[index];
    if (existing !== undefined) {
      return existing;
    }

    const glyph = this.scene.add.bitmapText(
      0,
      0,
      SYMBOL_SEQUENCE_FONT_KEY,
      "",
      SYMBOL_SEQUENCE_FONT_SIZE,
    );
    glyph.setOrigin(0, 0);
    glyph.setScrollFactor(0);
    glyph.setDepth(MESSAGE_TEXT_DEPTH);
    glyph.setTint(0xffffff);
    glyph.setVisible(false);
    glyph.setAlpha(0);
    this.glyphPool[index] = glyph;
    return glyph;
  }

  public destroy(): void {
    this.bg.destroy();
    for (const glyph of this.glyphPool) {
      glyph.destroy();
    }
  }
}
